import { css } from '@emotion/core';
import Button from 'react-bootstrap/Button';
import { connect } from 'react-redux';
import { useState, useEffect, useContext } from 'react';

import {
  halfSpacing, standardSpacing,
} from 'styles/global_defaults/scaffolding';
import {
  NvDropdown, NvDropdownTextItem, NvDropdownButtonStyle, NvDropdownAlign,
} from 'shared/components/inputs/nv-dropdown';
import moment, { Moment } from 'moment';
import { RootState } from 'redux/schemas';
import {
  getPeriodStartDate, getPeriodEndDate, getCurrentOrgCreationDate, noContractPeriodDefined,
} from 'redux/selectors/licenses';
import { warning, success } from 'styles/global_defaults/colors';
import {
  getCoursesLogins,
  getMonthlyEnrollments,
  getEnrollmentsForPeriod,
} from 'redux/actions/license';
import LoadingWrapper from 'shared/components/loading-wrapper';
import {
  EnrollmentForPeriod,
  EnrollmentPeriod,
} from 'redux/schemas/models/license';
import { MonthlyEnrollmentsResponse } from 'redux/schemas/api/license';
import t from 'react-translate';
import NvDatepicker, { DatePickerType } from 'shared/components/inputs/nv-datepicker';
import {
  desktop, notDesktop, notHandheld, handheld,
} from 'styles/global_defaults/media-queries';
import { AngularServicesContext } from 'react-app';

import { useForm, FormProvider } from 'react-hook-form';
import BarChart, { BarChartUnitData } from './bar-chart';
import RadialGauge, { Mode, HeaderStyle } from './radial-gauge';
import ExportButton from './export-button';

type StateProps = {
  licenseDataLoaded: boolean,
  // Whether or not a license period exists at all
  licensePeriodDefined: boolean,
  periodStartDate: moment.Moment,
  periodEndDate: moment.Moment,
  currentInstitutionId: number,
  orgCreationDate: moment.Moment,
  licensesUsed: number,
  // TODO: This total login and monthly enrollment info is specific to the currently selected date period.
  // We should probably record that date period in the redux state instead of keeping it local
  totalLogins: number,
  monthlyEnrollments: MonthlyEnrollmentsResponse,
  monthlyEnrollmentsLoaded: boolean,
  // Flat values for a single period instead of multiple as used in bar charts
  flatEnrollmentValues: EnrollmentForPeriod,
  flatEnrollmentValuesLoaded: boolean,
};

type OwnProps = {
  /** Callback that updates the current date period the the selection made in this panel */
  setPeriod: (startDate: moment.Moment, endDate: moment.Moment, periodType: EnrollmentPeriod, text: string) => void,
};

type DispatchProps = {
  getCoursesLogins,
  getMonthlyEnrollments,
  getEnrollmentsForPeriod,
};

interface PeriodDropdownItem extends NvDropdownTextItem {
  periodType: EnrollmentPeriod,
}

export const textForPeriod = (period: EnrollmentPeriod, date?: moment.Moment): string => {
  switch (period) {
    case EnrollmentPeriod.CURRENT_CONTRACT_PERIOD: return t.INSTITUTIONS.LICENSES.DASHBOARD.CURRENT_CONTRACT_PERIOD();
    case EnrollmentPeriod.SINCE_ORG_CREATION: return date ? t.INSTITUTIONS.LICENSES.DASHBOARD.ALL_DATA_SINCE(date.format('MM/DD/YYYY')) : '';
    case EnrollmentPeriod.PAST_12_MONTHS: return t.INSTITUTIONS.LICENSES.DASHBOARD.FROM_PAST_MONTHS(12);
    case EnrollmentPeriod.PAST_6_MONTHS: return t.INSTITUTIONS.LICENSES.DASHBOARD.FROM_PAST_MONTHS(6);
    case EnrollmentPeriod.PAST_30_DAYS: return t.INSTITUTIONS.LICENSES.DASHBOARD.FROM_PAST_DAYS(30);
    case EnrollmentPeriod.CUSTOM_DATE_RANGE: return t.INSTITUTIONS.LICENSES.DASHBOARD.WITHIN_A_DATE_RANGE();
    default: return '';
  }
};

// See https://novoed.atlassian.net/browse/NOV-61727. Range should be from beginning of the month 1 year ago, to
// the end of the current month
const adjustedDatesForMonthRange = (originalPeriodType: EnrollmentPeriod): [moment.Moment, moment.Moment] => {
  switch (originalPeriodType) {
    case EnrollmentPeriod.PAST_12_MONTHS: {
      const startDate = moment().subtract(11, 'months').startOf('month');
      const endDate = adjustDateToUTC(moment().endOf('month'));
      return [startDate, endDate];
    }
    case EnrollmentPeriod.PAST_6_MONTHS: {
      const startDate = moment().subtract(5, 'months').startOf('month');
      const endDate = adjustDateToUTC(moment().endOf('month'));
      return [startDate, endDate];
    }
    default:
      return [null, null];
  }
};

/**
 * NOTE: This is currently disabled to make no adjustment for UTC. The below behavior was initially desired
 * but the team has agreed to not normalize values on the frontend and listen for customer feedback before
 * implementing.
 *
 * Please remove in the future (after 01/23/2020)
 *
 * This adjusts a moment object to subtract out it's UTC offset. Generally, the frontend handles dates in the user's local
 * timezone and sends request data as UTC, and the backend only does calculations based on UTC. However, the backend needs to
 * aggregate data based on the user's month in local time.
 * This is a hack and likely needs adjusted after we devise a strategy for how to bin these months while respecting the local timezone
 * https://novoed.atlassian.net/browse/NOV-61862
 */
const adjustDateToUTC = (date: moment.Moment): moment.Moment => date; // date.add(date.utcOffset(), 'minutes');

export const defaultPeriod = adjustedDatesForMonthRange(EnrollmentPeriod.PAST_6_MONTHS);
export const defaultPeriodType = EnrollmentPeriod.PAST_6_MONTHS;

/**
 * The 'Enrollment' section of the License Dashboard page.
 * Features a dropdown for selecting date ranges, a 'total vs logged in' radial chart, and a
 * 'total vs new' chart. 'Total vs new' is a radial chart when the date range is 1 month or less,
 * and a bar chart otherwise
 */
const EnrollmentPanel = (props: StateProps & DispatchProps & OwnProps) => {
  const { $uibModal } = useContext(AngularServicesContext);

  const style = css`
    .charts {
      display: -ms-grid;
      display: grid;
      ${notHandheld(css`
        grid-template-columns: 300px 300px; /*repeat(auto-fit, 50vmin);*/
        -ms-grid-columns: 1fr (minmax(1fr, 400px))[2] 1fr;

        & > :nth-of-type(1) {
          -ms-grid-column: 2;
          -ms-grid-row: 1;
        }

        & > :nth-of-type(2) {
          -ms-grid-column: 3;
          -ms-grid-row: 1;
        }
      `)};

      ${handheld(css`
        grid-template-columns: repeat(auto-fit, minmax(354px, 1fr));
        -ms-grid-columns: (minmax(354px, 1fr))[1] 1fr;

        & > :nth-of-type(1) {
          -ms-grid-column: 1;
          -ms-grid-row: 1;
        }

        & > :nth-of-type(2) {
          -ms-grid-column: 1;
          -ms-grid-row: 2;
        }
      `)};
      justify-content: center;

      ${desktop(css`
        grid-column-gap: 100px;
      `)};

      ${notDesktop(css`
        grid-column-gap: ${standardSpacing}px;
      `)};

      & > * {
        margin-top: ${standardSpacing * 2}px;
      }
    }

    .nv-dropdown-container {
      margin-top: ${halfSpacing}px;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: flex-start;
    }

    .nv-dropdown, .date-selection-row {
      margin-top: ${halfSpacing}px;
    }

    .date-selection-row {
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      margin-bottom: ${standardSpacing}px;

      .react-datepicker-wrapper {
        width: 100px;
      }
    }
  `;

  // Bools tracking whether login and monthly enrollment data has finished loading
  const [currentLoginsReady, setCurrentLoginsReady] = useState(false);

  // The default start and end dates for the period. Defaults to the past 12 months if
  // undefined
  const [pStart, pEnd] = defaultPeriod;

  // State that tracks the current period start and end date selection
  const [periodStart, setPeriodStart] = useState(pStart);
  const [periodEnd, setPeriodEnd] = useState(pEnd);
  // Drafts for the start and end dates used by the custom date range selection option
  const [periodStartDraft, setPeriodStartDraft] = useState(props.periodStartDate);
  const [periodEndDraft, setPeriodEndDraft] = useState(props.periodEndDate);

  // Which type of period is selected in the dropdown
  const [periodType, setPeriodType] = useState(defaultPeriodType);

  // Shows/hides the date range selection UI
  const [dateSelectionVisible, setDateSelectionVisible] = useState(false);

  const showMonthlyEnrollments = periodEnd.diff(periodStart, 'days') > 60;

  // Convert redux monthly enrollment data into the barchart data format
  const [barChartData, setBarChartData]: [BarChartUnitData[], Function] = useState([]);

  // Keeps drafts and non-drafts in sync
  const updatePeriod = (startDate: moment.Moment, endDate: moment.Moment, enrollmentPeriod: EnrollmentPeriod) => {
    const newStartDate = startDate.startOf('day');
    const newEndDate = adjustDateToUTC(endDate.endOf('day'));

    setPeriodStartDraft(newStartDate);
    setPeriodEndDraft(newEndDate);
    setPeriodStart(newStartDate);
    setPeriodEnd(newEndDate);
    setPeriodType(enrollmentPeriod);

    const textDate = enrollmentPeriod === EnrollmentPeriod.SINCE_ORG_CREATION ? props.orgCreationDate : null;
    props.setPeriod(newStartDate, newEndDate, enrollmentPeriod, textForPeriod(enrollmentPeriod, textDate));
  };

  // Date range dropdown text & callbacks
  const createDropdownItems = (startDraft: moment.Moment, endDraft: moment.Moment): PeriodDropdownItem[] => [
    {
      type: 'text',
      text: textForPeriod(EnrollmentPeriod.CURRENT_CONTRACT_PERIOD),
      periodType: EnrollmentPeriod.CURRENT_CONTRACT_PERIOD,
      callback: (i) => { setDateSelectionVisible(false); selectPeriod(props.periodStartDate, props.periodEndDate, i); },
    },
    {
      type: 'text',
      text: textForPeriod(EnrollmentPeriod.PAST_12_MONTHS),
      periodType: EnrollmentPeriod.PAST_12_MONTHS,
      callback: (i) => {
        const [startDate, endDate] = adjustedDatesForMonthRange(EnrollmentPeriod.PAST_12_MONTHS);
        setDateSelectionVisible(false);
        selectPeriod(startDate, endDate, i);
      },
    },
    {
      type: 'text',
      text: textForPeriod(EnrollmentPeriod.PAST_6_MONTHS),
      periodType: EnrollmentPeriod.PAST_6_MONTHS,
      callback: (i) => {
        const [startDate, endDate] = adjustedDatesForMonthRange(EnrollmentPeriod.PAST_6_MONTHS);
        setDateSelectionVisible(false);
        selectPeriod(startDate, endDate, i);
      },
    },
    {
      type: 'text',
      text: textForPeriod(EnrollmentPeriod.PAST_30_DAYS),
      periodType: EnrollmentPeriod.PAST_30_DAYS,
      callback: (i) => {
        setDateSelectionVisible(false);
        selectPeriod(moment().subtract(30, 'days'), moment(), i);
      },
    },
    {
      type: 'text',
      text: textForPeriod(EnrollmentPeriod.SINCE_ORG_CREATION, props.orgCreationDate),
      periodType: EnrollmentPeriod.SINCE_ORG_CREATION,
      callback: (i) => { setDateSelectionVisible(false); selectPeriod(props.orgCreationDate, moment(), i); },
    },
    {
      type: 'text',
      text: textForPeriod(EnrollmentPeriod.CUSTOM_DATE_RANGE),
      periodType: EnrollmentPeriod.CUSTOM_DATE_RANGE,
      callback: (i) => {
        setDateSelectionVisible(true);
        selectPeriod(startDraft, endDraft, i);
      },
    },
  ];

  let dropdownData = createDropdownItems(periodStartDraft, periodEndDraft);

  // Remove the 'Current Contract Period' item when no license period exists since it's
  // nonsensical in that case
  if (!props.licensePeriodDefined) {
    dropdownData = dropdownData.slice(1);
  }

  const defaultDropdownIndex = dropdownData.findIndex(d => d.periodType === defaultPeriodType);

  // Called whenever the date period is changed
  const selectPeriod = (startDate: moment.Moment, endDate: moment.Moment, dropdownItem: PeriodDropdownItem) => {
    updatePeriod(startDate, endDate, dropdownItem.periodType);
  };

  useEffect(() => {
    updatePeriod(periodStart, periodEnd, periodType);
  }, [periodStart.format(), periodEnd.format(), props.licenseDataLoaded]);

  // Sets the default period and type upon page load after data license data finishes loading
  useEffect(() => {
    if (!props.licenseDataLoaded) {
      return;
    }

    updatePeriod(defaultPeriod[0], defaultPeriod[1], defaultPeriodType);
  }, [props.licenseDataLoaded]);

  // Loads chart data for this component
  useEffect(() => {
    // We have to check if periodStart and periodEnd exist because they will be null during page load
    if (props.currentInstitutionId && periodStart.isValid() && periodEnd.isValid()) {
      const formattedTimes = {
        startTime: periodStart.toISOString(),
        endTime: periodEnd.toISOString(),
      };

      props.getCoursesLogins({
        institutionId: props.currentInstitutionId,
        startTime: formattedTimes.startTime,
        endTime: formattedTimes.endTime,
      });

      if (showMonthlyEnrollments) {
        props.getMonthlyEnrollments({
          institutionId: props.currentInstitutionId,
          startTime: formattedTimes.startTime,
          endTime: formattedTimes.endTime,
          period: periodType,
        });
      } else {
        props.getEnrollmentsForPeriod({
          institutionId: props.currentInstitutionId,
          startTime: formattedTimes.startTime,
          endTime: formattedTimes.endTime,
          period: periodType,
        });
      }
    }
    // Note that we must use .format() here so this re-evaluates when the moment object's value is
    // different. Failure to do so causes this effect to fire constantly as our selector creates a
    // new moment object every time it's called
  }, [props.currentInstitutionId, periodStart.format(), periodEnd.format()]);

  // Check whether data is finished loading
  useEffect(() => {
    // TODO: Is it actually smart to check if the data is loaded this way?
    setCurrentLoginsReady(props.totalLogins !== null && props.licensesUsed !== null);
  }, [props.totalLogins, props.licensesUsed, props.monthlyEnrollments]);

  useEffect(() => {
    let data = [];
    Object.keys(props.monthlyEnrollments.monthlyEnrollments).forEach(month => {
      const monthDate = moment(month, 'M-YYYY');

      if (monthDate > moment()) {
        return;
      }

      const enrollmentData = props.monthlyEnrollments.monthlyEnrollments[month];
      data.push({
        // Convert the first two characters of the month string formatted like 03/19 into
        // a three-letter month abbreviation
        label: moment(month.substr(0, 2), 'MM').format('MMM'),
        active: enrollmentData.newEnrollment,
        total: enrollmentData.totalEnrollment,
      });
    });

    // Limit the bar chart to at most 12 months
    if (data.length > 12) {
      data = data.slice(data.length - 12);
    }

    setBarChartData(data);
  }, [props.monthlyEnrollments]);

  // Show a non %-based radial chart for the # of users enrolled if the end date before the current day.
  // We can't show detailed login metrics in this case b/c the backend doesn't keep a historical record of
  // user logins.
  function isBeforeToday(date) {
    return periodEnd.isBefore(moment(), 'day');
  }
  const showFlatEnrollmentValues = isBeforeToday(periodEnd);

  const methods = useForm({ mode: 'onChange', shouldUnregister: true });

  return (
    <div css={style} className='mx-auto'>
      <div className='text-xl text-center font-weight-light'>{t.INSTITUTIONS.LICENSES.DASHBOARD.ENROLLMENT()}</div>

      <div className='nv-dropdown-container'>
        <NvDropdown
          buttonStyle={NvDropdownButtonStyle.CONDENSED_TEXT}
          items={dropdownData}
          align={NvDropdownAlign.CENTER}
          initialIndex={defaultDropdownIndex}
          showSelectedIndicator
        />
      </div>

      { dateSelectionVisible && (
        <FormProvider {...methods}>
          <div className='date-selection-row'>
            <NvDatepicker
              name='startDate'
              type={DatePickerType.DATE}
              value={moment(periodStartDraft)}
              onChange={(val) => setPeriodStartDraft(moment(val))}
              max={moment(periodEndDraft).subtract(1, 'day')}
              placement='top-start'
            />
            <span className='font-weight-bold condensed px-2'>TO</span>
            <NvDatepicker
              name='startDate'
              type={DatePickerType.DATE}
              value={moment(periodEndDraft)}
              onChange={(val) => setPeriodEndDraft(moment(val))}
              min={moment(periodStartDraft).add(1, 'day')}
              placement='top-start'
            />
            <Button
              className='ml-2'
              variant='outline-primary'
              onClick={() => {
                selectPeriod(
                  periodStartDraft,
                  periodEndDraft,
                  {
                    type: 'text',
                    text: `From ${periodStartDraft.format('MM/DD/YYYY')} to ${periodEndDraft.format('MM/DD/YYYY')}`,
                    periodType: EnrollmentPeriod.CUSTOM_DATE_RANGE,
                  }
                  ,
                );
              }}
            >
              {t.USERS.REGISTRATION.UPDATE()}
            </Button>
          </div>
        </FormProvider>
      )}
      <ExportButton
        periodType={periodType}
        periodStart={periodStart}
        periodEnd={periodEnd}
      />
      <div className='charts'>
        {/* Total vs Logged in radial chart */}
        <LoadingWrapper isLoaded={currentLoginsReady}>
          <RadialGauge
            mode={Mode.FULL_CIRCLE}
            activeColor={warning}
            headerStyle={HeaderStyle.TOTAL_COUNT}
            currentLabel={showFlatEnrollmentValues ? t.INSTITUTIONS.LICENSES.DASHBOARD.TOTAL_ENROLLED() : t.INSTITUTIONS.LICENSES.DASHBOARD.LOGGED_IN()}
            currentLegendLabel={t.INSTITUTIONS.LICENSES.DASHBOARD.HAS_LOGGED_IN(props.totalLogins)}
            remainingLegendLabel={t.INSTITUTIONS.LICENSES.DASHBOARD.HAS_NOT_LOGGED_IN(props.licensesUsed - props.totalLogins)}
            min={0}
            max={props.licensesUsed}
            current={showFlatEnrollmentValues ? (props.licensesUsed ?? 0) : (props.totalLogins ?? 0)}
          // TODO: Combine these?
            isPct={!showFlatEnrollmentValues}
            currentDisabled={showFlatEnrollmentValues}
          />
        </LoadingWrapper>
        {/* Total vs New chart. Is a radial chart if period <= 1 month, and a bar char otherwise */}
        { showMonthlyEnrollments
          ? (
            <LoadingWrapper isLoaded={props.monthlyEnrollmentsLoaded && barChartData.length > 0}>
              <BarChart
                data={barChartData}
                totalLabel={t.INSTITUTIONS.LICENSES.DASHBOARD.TOTAL()}
                activeLabel={t.INSTITUTIONS.LICENSES.DASHBOARD.NEW()}
                dataName={t.INSTITUTIONS.LICENSES.DASHBOARD.ENROLLMENT()}
              />
            </LoadingWrapper>
          )
          : (
            <LoadingWrapper isLoaded={props.flatEnrollmentValuesLoaded}>
              <RadialGauge
                mode={Mode.NESTED_FULL_CIRCLE}
                activeColor={success}
                currentLabel={t.INSTITUTIONS.LICENSES.DASHBOARD.NEW()}
                currentLegendLabel={t.INSTITUTIONS.LICENSES.DASHBOARD.NEW_ENROLLMENTS(props.flatEnrollmentValues.newEnrollments)}
                remainingLegendLabel={t.INSTITUTIONS.LICENSES.DASHBOARD.TOTAL_ENROLLMENTS(props.flatEnrollmentValues.totalEnrollments)}
                min={0}
                max={props.flatEnrollmentValues.totalEnrollments}
                current={props?.flatEnrollmentValues?.newEnrollments ?? 0}
                isPct
                reverseLegendOrder
              />
            </LoadingWrapper>
          )}
      </div>
    </div>
  );
};

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  (state, ownProps) => ({
    licenseDataLoaded: state.models.license.loaded,
    licensePeriodDefined: !noContractPeriodDefined(state),
    periodStartDate: getPeriodStartDate(state),
    periodEndDate: getPeriodEndDate(state),
    currentInstitutionId: state.app.currentInstitutionId,
    licensesUsed: state.models.totalEnrollments,
    totalLogins: state.models.totalLogins,
    monthlyEnrollments: state.models.monthlyEnrollments,
    monthlyEnrollmentsLoaded: state.app.licenseDashboard.monthlyEnrollmentsLoaded,
    flatEnrollmentValues: state.models.enrollmentsForPeriod,
    flatEnrollmentValuesLoaded: state.app.licenseDashboard.enrollmentsForPeriodLoaded,
    orgCreationDate: getCurrentOrgCreationDate(state) ?? moment(),
    ...ownProps,
  }),
  {
    getCoursesLogins,
    getMonthlyEnrollments,
    getEnrollmentsForPeriod,
  },
)(EnrollmentPanel);
